import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    OutlinedInput,
} from "@mui/material";
import NavigationButton from "./NavigationButton";

interface SelectOption {
    value: string;
    label: string;
}

interface StyledSelectProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    options: SelectOption[];
    sx?: object;
    disabled?: boolean;
    isArrowEnabled?: boolean;
}

const StyledSelect = ({
    label,
    value,
    onChange,
    options,
    sx,
    disabled = false,
    isArrowEnabled = false,
}: StyledSelectProps) => {

    const handleChange = (event: SelectChangeEvent<string>) => {
        onChange(event.target.value);
    };

    const currentIndex = options.findIndex(opt => opt.value === value);

    const handlePrev = () => {
        if (currentIndex > 0) {
            onChange(options[currentIndex - 1].value);
        }
    };

    const handleNext = () => {
        if (currentIndex < options.length - 1) {
            onChange(options[currentIndex + 1].value);
        }
    };

    return (
        <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ mb: 3 }}
            className="custom-select"
        >
            {/* LEFT ARROW */}
            {isArrowEnabled && options?.length > 1 && (
                <NavigationButton
                    direction="left"
                    onClick={handlePrev}
                    disabled={disabled || currentIndex <= 0}
                />
            )}

            {/* SELECT */}
            <FormControl
                fullWidth
                size="small"
                sx={sx}
            >
                <InputLabel className="custom-label">{label}</InputLabel>

                <Select
                    value={value}
                    onChange={handleChange}
                    disabled={disabled}
                    input={<OutlinedInput label={label} />}
                    sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: disabled
                                ? 'rgba(0, 0, 0, 0.23)'
                                : 'rgba(0, 0, 0, 0.87)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: disabled
                                ? 'rgba(0, 0, 0, 0.23)'
                                : '#1976d2',
                        },
                        '& .MuiSelect-select': {
                            whiteSpace: 'normal !important',
                            overflow: 'visible !important',
                            textOverflow: 'unset !important',
                            lineHeight: 1.4,
                        },
                    }}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                maxHeight: 200,
                                '& .MuiMenuItem-root': {
                                    whiteSpace: 'normal !important',
                                    overflow: 'visible !important',
                                    textOverflow: 'unset !important',
                                    minHeight: 'auto !important',
                                    py: 1.5,
                                    lineHeight: 1.4,
                                },
                            },
                        },
                    }}
                >
                    {options.map(option => (
                        <MenuItem
                            key={option.value}
                            value={option.value}
                            sx={{
                                whiteSpace: "normal !important",
                                overflow: "visible !important",
                                textOverflow: "unset !important",
                                lineHeight: 1.4,
                                py: 1
                            }}
                        >
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* RIGHT ARROW */}
            {isArrowEnabled && options?.length > 1 && (
                <NavigationButton
                    direction="right"
                    onClick={handleNext}
                    disabled={disabled || currentIndex >= options.length - 1}
                />
            )}
        </Box>
    );
};

export default StyledSelect;
